import { useEffect, useState } from "react";
import { message } from "antd";
import axios from "axios";

export default function useStoryActors() {
  const [actors, setActors] = useState([]);
  const [loadingActors, setLoadingActors] =
    useState(false);
  const [savingActor, setSavingActor] =
    useState(false);

  // --------------------------------------------------
  // LOAD ACTORS
  // --------------------------------------------------

  const fetchActors = async () => {
    try {
      setLoadingActors(true);

      const res = await axios.get(
        "/api/actors"
      );

      if (res.data?.success) {
        const loadedActors =
          (res.data.actors || []).map((actor) => {
            let data = actor.data;

            // Depending on mysql driver,
            // JSON may already be parsed or
            // may arrive as a string.
            if (typeof data === "string") {
              try {
                data = JSON.parse(data);
              } catch (err) {
                console.error(
                  "Could not parse actor data:",
                  err
                );
                data = {};
              }
            }

            return {
              ...actor,
              data: data || {},
            };
          });

        setActors(loadedActors);
      }
    } catch (err) {
      console.error(
        "Failed to load actors:",
        err
      );

      message.error(
        "Actors konnten nicht geladen werden."
      );
    } finally {
      setLoadingActors(false);
    }
  };


   // --------------------------------------------------
  // UPDATE ACTOR
  // --------------------------------------------------



  const updateActor = async (
    actorId,
    name,
    svgFile
  ) => {
    try {
      const formData = new FormData();
  
      formData.append(
        "name",
        name.trim()
      );
  
      if (svgFile) {
        formData.append(
          "svg",
          svgFile,
          svgFile.name
        );
      }
  
      const response = await fetch(
        `/api/actors/${actorId}`,
        {
          method: "PUT",
          body: formData,
        }
      );
  
      if (!response.ok) {
        throw new Error(
          "Actor konnte nicht aktualisiert werden."
        );
      }
  
      const updatedActor =
        await response.json();
  
      // Update local state
      setActors((prev) =>
        prev.map((actor) =>
          actor.id === actorId
            ? updatedActor.actor
            : actor
        )
      );
  
      message.success(
        "Actor wurde aktualisiert."
      );
  
      return updatedActor.actor;
    } catch (error) {
      console.error(
        "Error updating actor:",
        error
      );
  
      message.error(
        "Actor konnte nicht aktualisiert werden."
      );
  
      return null;
    }
  };





  // --------------------------------------------------
  // SAVE ACTOR
  // --------------------------------------------------

  const saveActor = async (
    name,
    svgFile
  ) => {
    if (!name?.trim()) {
      message.warning(
        "Bitte einen Namen für den Actor eingeben."
      );
      return null;
    }

    if (!svgFile) {
      message.warning(
        "Bitte eine SVG-Datei auswählen."
      );
      return null;
    }

    try {
      setSavingActor(true);

      const formData = new FormData();

      /*
       * Your existing backend expects:
       *
       * req.body.name
       * req.body.actor
       * req.files
       *
       * so we keep the actor JSON.
       */

      const actor = {
        rig: "biped-v1",
        svg: svgFile.name,
      };

      formData.append(
        "name",
        name.trim()
      );

      formData.append(
        "actor",
        JSON.stringify(actor)
      );

      formData.append(
        "svg",
        svgFile,
        svgFile.name
      );

      const res = await axios.post(
        "/api/actor",
        formData
      );

      if (
        res.data?.success &&
        res.data.actor
      ) {
        await fetchActors();

        message.success(
          "Actor gespeichert."
        );

        return res.data.actor;
      }

      return null;

    } catch (err) {
      console.error(
        "Failed to save actor:",
        err
      );

      message.error(
        "Actor konnte nicht gespeichert werden."
      );

      return null;

    } finally {
      setSavingActor(false);
    }
  };







  // --------------------------------------------------
// DELETE ACTOR
// --------------------------------------------------

const deleteActor = async (actorId) => {
  try {

    const response = await axios.delete(
      `/api/actors/${actorId}`
    );

    if (!response.data?.success) {
      throw new Error(
        "Actor konnte nicht gelöscht werden."
      );
    }

    // Remove from local state
    setActors((prev) =>
      prev.filter(
        (actor) => actor.id !== actorId
      )
    );

    message.success(
      "Actor wurde gelöscht."
    );

    return true;

  } catch (error) {

    console.error(
      "Error deleting actor:",
      error
    );

    // Actor is still being used
    if (
      error.response?.status === 409
    ) {
      const data =
        error.response.data;

      if (
        data.characters &&
        data.characters.length > 0
      ) {

        const characterNames =
          data.characters
            .map(
              (character) =>
                character.name
            )
            .join(", ");

        message.error(
          `Actor "${data.actorName}" kann nicht gelöscht werden. Er wird von folgenden Characters verwendet: ${characterNames}`
        );

      } else {

        message.error(
          data.message ||
          "Actor wird noch verwendet."
        );
      }

    } else if (
      error.response?.status === 404
    ) {

      message.error(
        "Actor wurde nicht gefunden."
      );

    } else {

      message.error(
        "Actor konnte nicht gelöscht werden."
      );
    }

    return false;
  }
};















  useEffect(() => {
    fetchActors();
  }, []);

  return {
    actors,
    loadingActors,
    savingActor,
   
    updateActor,
    deleteActor,

    fetchActors,
    saveActor,
  };
}